import React from 'react';
import {
  Box, IconButton, Table, TableHead, TableRow, TableCell, TableBody, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SectionCard from '../SectionCard';
import InvButton from '../InvButton';
import { InvTextField } from '../InvField';
import InvItemAutocomplete from '../InvItemAutocomplete';
import { emptyRfqLine } from '../../utils/rfqEntity';
import { IPD_COLORS } from '../../theme';

const CELL = { py: 1.25, verticalAlign: 'middle' };

export default function RfqLineItemsEditor({
  lines,
  onChange,
  getBaseUomCode,
  defaultRequiredByDate = '',
  disabled = false,
}) {
  function addLine(afterIndex) {
    const row = { ...emptyRfqLine() };
    if (afterIndex === undefined) {
      onChange([...lines, row]);
      return;
    }
    const next = [...lines];
    next.splice(afterIndex + 1, 0, row);
    onChange(next);
  }

  function removeLine(index) {
    if (lines.length <= 1) return;
    onChange(lines.filter((_, i) => i !== index));
  }

  function updateLine(index, patch) {
    onChange(lines.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function onItemPick(index, itemId, item) {
    const uomId = item?.base_uom_id || item?.uom_id || '';
    const uomCode = getBaseUomCode?.(itemId) || item?.base_uom_code || item?.uom_code || '';
    updateLine(index, {
      item_id: itemId,
      uom_id: uomId,
      uom_code: uomCode,
      required_by_date: lines[index]?.required_by_date || defaultRequiredByDate || '',
    });
  }

  const validCount = lines.filter((l) => l.item_id && l.required_quantity).length;

  return (
    <SectionCard
      title="Line Items"
      actions={(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
            {validCount} item{validCount !== 1 ? 's' : ''} ready
          </Typography>
          {!disabled && (
            <InvButton size="small" startIcon={<AddIcon />} onClick={() => addLine()}>
              Add Row
            </InvButton>
          )}
        </Box>
      )}
    >
      <Box sx={{ overflowX: 'auto' }}>
        <Table
          size="small"
          sx={{
            minWidth: 720,
            '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 36 }}>#</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 220 }}>Item *</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 160 }}>Specification</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 100 }}>Qty *</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 72 }}>UOM</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 120 }}>Required By</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 100 }}>Remarks</TableCell>
              <TableCell sx={{ width: 44 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: 'center', color: IPD_COLORS.textSecondary, py: 4 }}>
                  Add at least one item from Item Master
                </TableCell>
              </TableRow>
            ) : lines.map((line, i) => (
              <TableRow key={i}>
                <TableCell sx={CELL}>{i + 1}</TableCell>
                <TableCell sx={CELL}>
                  <InvItemAutocomplete
                    value={line.item_id}
                    onChange={(id, item) => onItemPick(i, id, item)}
                    placeholder="Search item…"
                  />
                </TableCell>
                <TableCell sx={CELL}>
                  <InvTextField
                    value={line.specification}
                    label=""
                    placeholder="Brand, grade, size…"
                    disabled={disabled}
                    onChange={(e) => updateLine(i, { specification: e.target.value })}
                  />
                </TableCell>
                <TableCell sx={CELL}>
                  <InvTextField
                    value={line.required_quantity}
                    label=""
                    type="number"
                    inputProps={{ min: 0, step: 'any' }}
                    disabled={disabled}
                    onChange={(e) => updateLine(i, { required_quantity: e.target.value })}
                  />
                </TableCell>
                <TableCell sx={{ ...CELL, color: IPD_COLORS.textSecondary, fontSize: 13 }}>
                  {line.uom_code || '—'}
                </TableCell>
                <TableCell sx={CELL}>
                  <InvTextField
                    value={line.required_by_date}
                    label=""
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    disabled={disabled}
                    onChange={(e) => updateLine(i, { required_by_date: e.target.value })}
                  />
                </TableCell>
                <TableCell sx={CELL}>
                  <InvTextField
                    value={line.remarks}
                    label=""
                    disabled={disabled}
                    onChange={(e) => updateLine(i, { remarks: e.target.value })}
                  />
                </TableCell>
                <TableCell sx={CELL}>
                  {!disabled && (
                    <IconButton
                      size="small"
                      aria-label="Remove row"
                      disabled={lines.length <= 1}
                      onClick={() => removeLine(i)}
                      sx={{ color: IPD_COLORS.error.text }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </SectionCard>
  );
}
